import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useChatSecurity } from '../provider/ChatSecurityProvider';
import { useWalletProvider } from '../walletprovider/useWalletProvider';
function toHexMessage(message) {
    return `0x${Array.from(new TextEncoder().encode(message), (byte) => byte.toString(16).padStart(2, '0')).join('')}`;
}
export default function WalletDeviceManager() {
    const { t } = useTranslation();
    const { selectedAccount, selectedWallet } = useWalletProvider();
    const security = useChatSecurity();
    const [devices, setDevices] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const sign = async (message) => {
        if (!selectedAccount || !selectedWallet?.provider)
            throw new Error('Wallet is not connected');
        const result = await selectedWallet.provider.request({
            method: 'personal_sign',
            params: [toHexMessage(message), selectedAccount.toLowerCase()],
        });
        if (typeof result !== 'string' || !result)
            throw new Error('Wallet did not return a signature');
        return result;
    };
    const refresh = async () => {
        if (!selectedAccount)
            return;
        setBusy(true);
        setError(null);
        try {
            setDevices(await security.listDevices(sign));
        }
        catch (cause) {
            setError(cause instanceof Error ? cause.message : 'Could not load registered devices');
        }
        finally {
            setBusy(false);
        }
    };
    const revoke = async (device) => {
        if (!selectedAccount || device.deviceId === security.deviceId)
            return;
        const confirmed = window.confirm(t('settings.chatSecurity.revokeConfirm'));
        if (!confirmed)
            return;
        setBusy(true);
        setError(null);
        try {
            await security.revokeDevice(device.deviceId, sign);
            await refresh();
        }
        catch (cause) {
            setError(cause instanceof Error ? cause.message : 'Could not revoke device');
        }
        finally {
            setBusy(false);
        }
    };
    return (_jsxs("section", { "aria-labelledby": "chat-security-devices-title", style: {
            marginBottom: 24, padding: 20, border: '1px solid rgba(45, 212, 191, .28)',
            borderRadius: 12, background: 'rgba(8, 20, 31, .72)', color: '#e5eef5',
        }, children: [_jsx("h2", { id: "chat-security-devices-title", style: { fontSize: 18, marginBottom: 8 }, children: t('settings.chatSecurity.title') }), _jsx("p", { style: { color: '#a9bbc9', marginBottom: 14 }, children: t('settings.chatSecurity.description') }), _jsxs("div", { style: { display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }, children: [_jsx("button", { type: "button", className: "button outline", disabled: busy || !selectedAccount, onClick: refresh, children: busy ? t('settings.chatSecurity.loading') : t('settings.chatSecurity.load') }), _jsx("span", { "aria-live": "polite", style: { color: security.status === 'ready' ? '#5eead4' : '#fbbf24' }, children: security.status === 'ready'
                            ? t('settings.chatSecurity.ready')
                            : t('settings.chatSecurity.notReady') })] }), error && _jsx("p", { role: "alert", style: { color: '#fca5a5', marginTop: 12 }, children: error }), devices && (_jsx("ul", { style: { listStyle: 'none', padding: 0, margin: '16px 0 0' }, children: devices.map((device) => {
                    const current = device.deviceId === security.deviceId;
                    return (_jsxs("li", { style: {
                            display: 'flex', justifyContent: 'space-between', gap: 12, padding: '12px 0',
                            borderTop: '1px solid rgba(148, 163, 184, .18)',
                        }, children: [_jsxs("div", { children: [_jsx("strong", { children: current ? t('settings.chatSecurity.thisDevice') : t('settings.chatSecurity.device') }), _jsxs("div", { style: { color: '#8fa6b7', fontSize: 13 }, children: [new Date(device.createdAt).toLocaleString(), " \u00B7 ", device.revokedAt
                                                ? t('settings.chatSecurity.revoked')
                                                : t('settings.chatSecurity.active')] })] }), !current && !device.revokedAt && (_jsx("button", { type: "button", className: "button outline", disabled: busy, onClick: () => revoke(device), children: t('settings.chatSecurity.revoke') }))] }, device.deviceId));
                }) }))] }));
}
